import type { RootState } from "../state";
import { getDefaultRoomState } from "../state";

// 此文件包含所有与状态持久化相关的action

export function saveStateToStorage(this: RootState & { [key: string]: any }) {
  try {
    const stateToSave = {
      roomStates: {},
      currentRoomId: this.currentRoomId
    };

    // 只保存必要的状态数据，排除不可序列化的部分
    Array.from(this.roomStates.entries()).forEach(([roomId, state]) => {
      if (state) {
        try {
          // 安全的状态克隆，严格排除所有可能导致循环引用的对象
          stateToSave.roomStates[roomId] = {
            // 基本状态
            roomId: state.roomId,
            // 关键修复：如果直播已结束，不保存队列和播放状态
            queue:
              state.productList?.status === "ended" ? [] : state.queue || [],
            currentIndex:
              state.productList?.status === "ended"
                ? 0
                : state.currentIndex || 0,
            isPlaying:
              state.productList?.status === "ended"
                ? false
                : state.isPlaying || false,
            currentAudioTime:
              state.productList?.status === "ended"
                ? undefined
                : state.currentAudioTime,
            currentAudioState:
              state.productList?.status === "ended"
                ? undefined
                : state.currentAudioState,

            // 产品信息
            productList: state.productList,
            selectedProductId: state.selectedProductId,
            productDetail: state.productDetail,
            selectedProductIds: state.selectedProductIds,
            pendingProductSwitch: state.pendingProductSwitch,

            // 连接状态
            sessionId: state.sessionId,
            externalRoomId: (state as any).externalRoomId,
            isBound: state.isBound,
            isConnecting: state.isConnecting,

            // 消息相关
            // 关键修复：如果直播已结束，不保存公屏消息
            publicMessages:
              state.productList?.status === "ended"
                ? []
                : state.publicMessages || [],
            sendMsg: state.sendMsg,

            // 音色相关
            voiceLangs: state.voiceLangs || [],
            voiceLang: state.voiceLang,
            voiceGender: state.voiceGender,
            voiceList: state.voiceList || [],
            selectedVoiceId: state.selectedVoiceId,
            selectedVoice: state.selectedVoice,
            currentVoicePage: state.currentVoicePage,
            voicePageSize: state.voicePageSize,
            activeLanguageIdx: state.activeLanguageIdx,

            // 界面状态
            isEditing: state.isEditing,
            roomLink: state.roomLink,
            livePlatform: state.livePlatform,
            platforms: state.platforms || [],
            isNearBottom: (state as any).isNearBottom,

            // 加载状态
            bindLinkLoading: state.bindLinkLoading,
            sendMessageLoading: state.sendMessageLoading,
            saveConfigLoading: state.saveConfigLoading,
            startLiveLoading: state.startLiveLoading,
            endLiveLoading: state.endLiveLoading,
            simulateCommentLoading: state.simulateCommentLoading,

            // 其他状态
            // 旧播放器属性已移除，使用 Howler.js 统一管理
            wasPlayingBeforeHidden: state.wasPlayingBeforeHidden,
            currentTimeBeforeHidden: state.currentTimeBeforeHidden,
            lastRecoveryTime: state.lastRecoveryTime,
            isManuallyUnbound: state.isManuallyUnbound,
            commentWsReconnectCount: state.commentWsReconnectCount,
            audioQueueWsReconnectCount: state.audioQueueWsReconnectCount,
            needsReconnect: state.needsReconnect,
            isPageRefreshed: state.isPageRefreshed,
            readyToRestore: state.readyToRestore,
            isInitialized: (state as any).isInitialized,
            lastSaveTime: (state as any).lastSaveTime,
            lastLogTime: state.lastLogTime || {},
            needsPowerSaveRecovery: state.needsPowerSaveRecovery,
            isRestoringPlayback: (state as any).isRestoringPlayback,
            // 轮播参数
            carouselInterval: (state as any).carouselInterval,
            isCarouselPlaying: (state as any).isCarouselPlaying,

            // 明确排除所有不可序列化的对象（设置为null而不是undefined）
            liveRoomRef: null,
            publicInfoRef: null,
            queueRef: null,
            commentWs: null,
            audioQueueWs: null,
            highPerformanceQueue: null,
            howlerPlayer: null,
            commentWsReconnectTimer: null,
            audioQueueWsReconnectTimer: null
          };

          if (
            state.queue?.length > 0 &&
            state.currentIndex < state.queue.length
          ) {
            const currentItem = state.queue[state.currentIndex];
            // console.log(
            //   `   - 当前项: "${currentItem?.text?.substring(0, 30)}..."`
            // );
            // console.log(
            //   `   - 播放进度: ${currentItem?.playedCharCount || 0}/${currentItem?.text?.length || 0}`
            // );
          }
        } catch (roomError) {
          console.error(`保存房间${roomId}状态失败:`, roomError);
          // 跳过这个房间，继续处理其他房间
        }
      }
    });

    // 在序列化前进行最后的验证
    const jsonString = JSON.stringify(stateToSave);
    localStorage.setItem("liveRoom-state", jsonString);
    // console.log(
    //   `状态已保存到localStorage，房间数量: ${Object.keys(stateToSave.roomStates).length}, 数据大小: ${jsonString.length} 字符`
    // );
  } catch (error) {
    console.error("保存直播间状态失败:", error);
    // 如果保存失败，尝试清空可能损坏的数据
    try {
      localStorage.removeItem("liveRoom-state");
    } catch {
      // 忽略清空错误
    }
  }
  return false;
}

export function loadStateFromStorage(this: RootState & { [key: string]: any }) {
  try {
    const savedState = localStorage.getItem("liveRoom-state");
    if (savedState) {
      const parsedState = JSON.parse(savedState);

      // 恢复房间状态
      Object.keys(parsedState.roomStates || {}).forEach(roomId => {
        // console.log(`开始恢复房间${roomId}状态...`);
        const savedRoomState = parsedState.roomStates[roomId];
        // console.log(`📂 从localStorage恢复的数据:`);
        // console.log(`   - 队列长度: ${savedRoomState.queue?.length || 0}`);
        // console.log(`   - 当前索引: ${savedRoomState.currentIndex}`);
        // console.log(`   - 播放状态: ${savedRoomState.isPlaying}`);
        // console.log(`   - 房间状态: ${savedRoomState.productList?.status}`);

        if (
          savedRoomState.queue?.length > 0 &&
          savedRoomState.currentIndex < savedRoomState.queue.length
        ) {
          const currentItem = savedRoomState.queue[savedRoomState.currentIndex];
          // console.log(
          //   `   - 当前项: "${currentItem?.text?.substring(0, 30)}..."`
          // );
          // console.log(
          //   `   - 播放进度: ${currentItem?.playedCharCount || 0}/${currentItem?.text?.length || 0}`
          // );
        }

        this.roomStates.set(roomId, {
          ...getDefaultRoomState(),
          ...parsedState.roomStates[roomId]
        });

        const restoredState = this.roomStates.get(roomId);

        // 重置播放状态和loading状态，确保刷新后能正常播放和操作
        const state = this.roomStates.get(roomId);
        if (state) {
          // 修复：保持正确的播放状态，如果房间是直播中且有队列，应该恢复播放
          const wasPlaying =
            state.isPlaying &&
            state.productList?.status === "live" &&
            state.queue?.length > 0;

          // 关键修复：如果直播已结束或状态不是live，清空播放时间和播放状态
          if (state.productList?.status !== "live") {
            state.isPlaying = false;
            state.currentAudioTime = undefined;
            state.queue = [];
            state.currentIndex = 0;
          } else {
            state.isPlaying = wasPlaying; // 保持播放状态以便后续恢复
          }
          (state as any).howlerPlayer = null;

          // 修复：强制重置所有loading状态，避免按钮卡在loading状态
          state.startLiveLoading = false;
          state.endLiveLoading = false;
          state.saveConfigLoading = false;
          state.sendMessageLoading = false;
          state.simulateCommentLoading = false;
          state.bindLinkLoading = false;
          state.cardLoadingMap = {};

          // 修复：刷新后重置用户交互状态，避免立即显示音频激活弹窗
          // 旧播放器属性已移除，使用 Howler.js 统一管理

          // 重置队列状态，但严格保持播放进度
          if (state.queue && state.queue.length > 0) {
            // 检查是否有正在播放的项目，保持所有播放进度
            let hasPlayingItem = false;
            let currentPlayingItem = null;

            // 🔧 关键修复：检测是否处于产品切换状态，如果是则清除音频播放进度
            const isProductSwitching = !!(
              state.pendingProductSwitch ||
              state.switchingProductId ||
              state.lastProductSwitchTime
            );

            state.queue.forEach((item, index) => {
              if (item.status === "playing") {
                state.currentIndex = index;
                hasPlayingItem = true;
                currentPlayingItem = item;
                // 重要：保持播放进度，绝对不要重置 playedCharCount
                item.status = "pending"; // 重置为pending，准备继续播放

                // 修复：不要限制播放进度，保持原始值
                // 删除进度限制逻辑，完全保持原有进度
              } else if (index < state.currentIndex) {
                item.status = "played";
                // 修复：已完成的项目不要强制设置进度，保持原有进度
              } else {
                item.status = "pending";

                // 🔧 关键修复：如果处于产品切换状态，清除音频播放进度防止新产品从中间开始播放
                if (isProductSwitching) {
                  // 清除可能导致中间播放的进度数据
                  delete (item as any).audioTimeProgress;
                  delete (item as any).audioCurrentTime;
                  delete (item as any).lastSavedProgress;
                  item.playedCharCount = 0;
                } else {
                  // 修复：未开始的项目也保持原有进度，不要重置为0
                }
              }
            });
          }
        }
      });

      // 恢复当前房间ID（如果当前不在直播间页面，则不恢复）
      const currentPath = window.location.pathname;
      const isInLiveRoomList =
        currentPath === "/myLiveRoom" || currentPath === "/myLiveRoom/";
      const isInLiveRoomDetail =
        currentPath.includes("/myLiveRoom") && !isInLiveRoomList;

      if (isInLiveRoomDetail) {
        // 修复：在直播间详情页时，从URL中提取房间ID
        const roomIdMatch = currentPath.match(/\/myLiveRoom\/(.+)/);
        if (roomIdMatch && roomIdMatch[1]) {
          this.currentRoomId = roomIdMatch[1];
        } else {
          this.currentRoomId = parsedState.currentRoomId;
        }
      } else {
        this.currentRoomId = null;
      }

      return true;
    }
  } catch (error) {
    console.error("恢复直播间状态失败:", error);
  }
  return false;
}

// 仅轮播相关的持久化/恢复（供组件直接调用）
export function persistCarouselForCurrentRoom(
  this: RootState & { [key: string]: any }
) {
  try {
    this.saveStateToStorage();
    return true;
  } catch {
    return false;
  }
}

export function restoreCarouselForCurrentRoom(
  this: RootState & { [key: string]: any }
) {
  try {
    const savedState = localStorage.getItem("liveRoom-state");
    if (!savedState) return false;
    const parsed = JSON.parse(savedState);
    const roomId =
      this.currentRoomId ||
      this.currentRoomState?.roomId ||
      this.currentRoomState?.productList?.id;
    if (!roomId) return false;
    const savedRoom = parsed?.roomStates?.[roomId];
    if (!savedRoom || !this.currentRoomState) return false;
    if (typeof savedRoom.carouselInterval === "string") {
      this.currentRoomState.carouselInterval = savedRoom.carouselInterval;
    }
    if (typeof savedRoom.isCarouselPlaying === "boolean") {
      this.currentRoomState.isCarouselPlaying = savedRoom.isCarouselPlaying;
    }
    return true;
  } catch {
    return false;
  }
}